import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Sidebar } from "@/components/Sidebar";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Menu } from "lucide-react";
import api, { getPendingRequestCount, subscribeToApiActivity } from "@/lib/api";
import { useTheme } from "@/lib/theme";
import { MiniStatSkeleton, Skeleton } from "@/components/ui/kit";

const titles = {
  "/": "Overview",
  "/accounts": "Telegram Accounts",
  "/channels": "Channels",
  "/channel-scanner": "Channel Scanner",
  "/rules": "Text Replacements",
  "/branding": "Branding & Watermark",
  "/sandbox": "Preview Sandbox",
  "/jobs": "Copy Jobs",
  "/vault": "Content Vault",
  "/queue": "Job Queue",
  "/notifications": "Notifications",
};

function connectionText(s) {
  if (s.reconnecting) return "Reconnecting…";
  if (s.total === 0) return "No accounts";
  return `${s.connected}/${s.total} connected`;
}

function connectionColor(s) {
  if (s.reconnecting) return "var(--c-amber)";
  if (s.connected > 0) return "var(--c-emerald)";
  return "var(--c-rose)";
}

const RouteLoadingShell = () => (
  <div
    className="absolute inset-0 z-20 bg-[var(--tg-bg)]"
    data-testid="route-loading"
    role="status"
    aria-label="Loading section"
  >
    <div className="space-y-5 animate-in fade-in duration-100">
      <div className="flex items-center justify-between gap-4">
        <div className="space-y-2">
          <Skeleton className="h-6 w-44" />
          <Skeleton className="h-3 w-64 max-w-[60vw]" />
        </div>
        <Skeleton className="h-9 w-28 rounded-md" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {Array.from({ length: 4 }).map((_, index) => <MiniStatSkeleton key={index} />)}
      </div>
      <div className="rounded-xl border border-[var(--tg-border)] bg-[var(--tg-surface)] overflow-hidden">
        <div className="flex gap-5 px-4 py-3 border-b border-[var(--tg-border)]">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-3 w-28" />
        </div>
        <div className="p-4 space-y-3">
          {Array.from({ length: 7 }).map((_, index) => (
            <div key={index} className="flex items-center gap-4">
              <Skeleton className="h-4 w-4 rounded" />
              <Skeleton className="h-3 flex-1 max-w-lg" />
              <Skeleton className="h-3 w-20 ml-auto" />
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const RoutedContent = ({ pathname }) => {
  const [loading, setLoading] = useState(false);
  const transitionRef = useRef(0);
  const sawRequestRef = useRef(false);

  useLayoutEffect(() => {
    const transition = ++transitionRef.current;
    sawRequestRef.current = getPendingRequestCount() > 0;
    setLoading(true);
    let settleFrame = 0;
    const finishAfterCommit = () => {
      cancelAnimationFrame(settleFrame);
      settleFrame = requestAnimationFrame(() => {
        settleFrame = requestAnimationFrame(() => {
          if (transitionRef.current === transition) setLoading(false);
        });
      });
    };
    const unsubscribe = subscribeToApiActivity((pending) => {
      if (transitionRef.current !== transition) return;
      if (pending > 0) sawRequestRef.current = true;
      if (sawRequestRef.current && pending === 0) finishAfterCommit();
    });
    // Static sections do not issue a request. Give their effects one turn to
    // start one; otherwise reveal them promptly instead of leaving a cover up.
    const noRequestFallback = setTimeout(() => {
      if (transitionRef.current === transition && !sawRequestRef.current) finishAfterCommit();
    }, 120);
    return () => {
      unsubscribe();
      clearTimeout(noRequestFallback);
      cancelAnimationFrame(settleFrame);
    };
  }, [pathname]);

  return (
    <div className="relative min-h-[calc(100vh-8rem)]">
      <Outlet />
      {loading && <RouteLoadingShell />}
    </div>
  );
};

export const Layout = () => {
  const loc = useLocation();
  const [summary, setSummary] = useState({ total: 0, connected: 0, reconnecting: false });
  const [menuOpen, setMenuOpen] = useState(false);
  const { mode, setMode } = useTheme();

  useEffect(() => {
    let active = true;
    const poll = async () => {
      try {
        const { data } = await api.get("/accounts");
        if (active)
          setSummary({
            total: data.length,
            connected: data.filter((a) => a.authorized).length,
            reconnecting: data.some((a) => a.reconnecting),
          });
      } catch (e) {
        console.error("account summary poll failed", e);
      }
    };
    poll();
    const t = setInterval(poll, 15000);
    return () => {
      active = false;
      clearInterval(t);
    };
  }, []);

  const connText = connectionText(summary);
  const dotColor = connectionColor(summary);

  return (
    <div className="flex min-h-screen noise-bg">
      {menuOpen && (
        <div
          onClick={() => setMenuOpen(false)}
          data-testid="sidebar-backdrop"
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm md:hidden"
        />
      )}
      <Sidebar open={menuOpen} onClose={() => setMenuOpen(false)} />
      <div className="flex-1 flex flex-col min-w-0 relative z-10">
        <header className="h-16 sticky top-0 z-30 flex items-center justify-between px-4 sm:px-8 border-b border-[var(--tg-border)] bg-[rgb(var(--tg-bg-rgb)/0.80)] backdrop-blur-xl">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => setMenuOpen(true)}
              data-testid="menu-toggle"
              className="md:hidden text-[var(--tg-muted)] hover:text-[var(--tg-strong)] transition-colors shrink-0"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>
            <div className="min-w-0">
              <div className="label-caps">Dashboard</div>
              <h1 className="font-head font-bold text-lg sm:text-xl tracking-tight -mt-0.5 truncate" data-testid="page-title">
                {titles[loc.pathname] || "CloneCast"}
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div
              className="flex items-center gap-2.5 px-3.5 py-2 rounded-full border border-[var(--tg-border)] bg-[var(--tg-surface)]"
              data-testid="connection-badge"
            >
              <span
                className={summary.connected > 0 && !summary.reconnecting ? "live-dot h-2 w-2 rounded-full" : "h-2 w-2 rounded-full"}
                style={{ background: dotColor }}
              />
              <span className="text-xs font-medium text-[var(--tg-muted)] hidden sm:inline">{connText}</span>
            </div>
            <ThemeToggle mode={mode} setMode={setMode} />
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-8">
          <RoutedContent pathname={loc.pathname} />
        </main>
      </div>
    </div>
  );
};
